/**
 * Authenticated user with zero workspace memberships lands here.
 *
 * Rendered inside `ProtectedRoute` at `/no-workspace`. Deliberately does
 * NOT use `AppShell` — AppShell is workspace-scoped and would 404 (or
 * misbehave) without a `workspaceId` in the URL. This page has its own
 * minimal authenticated header and a Logout action.
 *
 * Discipline:
 *   - NEVER renders the string "Sign in" — the page is only ever shown
 *     to authenticated users, so a "Sign in" affordance would be false.
 *   - Does NOT synthesise a workspace or membership.
 *   - Does NOT create an invite; regular users must be added by an
 *     existing workspace admin (out-of-band).
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";

export default function NoWorkspacePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const isPlatformAdmin = Boolean(user && user.is_platform_admin);

  async function onLogout() {
    await logout();
    navigate("/", { replace: true });
  }

  return (
    <div
      className="min-h-screen bg-slate-50"
      data-testid="no-workspace-page"
    >
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto max-w-4xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span
              className="inline-flex h-8 w-8 items-center justify-center rounded bg-slate-900 text-white text-xs font-bold tracking-widest"
              aria-hidden="true"
            >
              RA
            </span>
            <span className="text-sm font-semibold text-slate-900">
              RecoveryAssure
            </span>
          </div>
          <div className="flex items-center gap-3">
            {user?.email && (
              <span
                className="text-xs text-slate-600 font-mono"
                data-testid="no-workspace-email"
              >
                {user.email}
              </span>
            )}
            <button
              type="button"
              onClick={onLogout}
              data-testid="no-workspace-logout-button"
              className="rounded border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-800 hover:bg-slate-100"
              aria-label="Log out"
            >
              Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-16 space-y-6">
        {isPlatformAdmin ? (
          <>
            <div
              className="inline-flex items-center gap-2 rounded bg-slate-900 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-white"
              data-testid="no-workspace-platform-admin-badge"
            >
              Platform admin
            </div>
            <h1
              className="text-2xl font-semibold text-slate-900"
              data-testid="no-workspace-heading"
            >
              Signed in as Platform Admin
            </h1>
            <p className="text-sm text-slate-600 leading-relaxed">
              You are signed in with platform admin privileges. No workspace
              is currently assigned to your account. Platform administration
              surfaces will appear here as they are added.
            </p>
          </>
        ) : (
          <>
            <h1
              className="text-2xl font-semibold text-slate-900"
              data-testid="no-workspace-heading"
            >
              Signed in — no workspace assigned
            </h1>
            <p className="text-sm text-slate-600 leading-relaxed">
              You are signed in but have no workspace access yet. Contact
              your workspace administrator to be invited.
            </p>
          </>
        )}
        <div className="pt-4 text-xs text-slate-500">
          Session cookie is set. If you were expecting a dashboard, ask
          your workspace admin to add your email to the correct workspace,
          then reload this page.
        </div>
      </main>
    </div>
  );
}
